import React, { useEffect, useMemo } from 'react'
import { Spinner } from 'react-bootstrap'
import styles from './styles/profile-header.module.scss'
import Avatar from 'components/profile/Avatar'
import i18n from 'constants/strings'
import WTNDefinition from './WTNDefinition'
import { Flag } from 'components/generic/Country'
import {
  getAgeReference,
  getOwnProfileUrl,
  getProfileId,
  getSignInUrl,
  getUnknownDisplayText,
  isPlayerHidden,
} from 'utils'
import {
  MY_SOCIAL_MATCHES_KEY,
  PersonIDEnum,
  PersonIDInput,
  ProfileSection,
  QueryParamNames,
} from 'types/global'
import { loadProfile, updateProfileAvatar, uploadProfileAvatar } from 'actions/profile-actions'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import { useCountries } from 'hooks/use-countries'
import MainContainer from 'components/generic/MainContainer'
import { WTNScaleData } from '@clubspark-react/clubspark-itf-shared-components'
import { getGraphQlApiEndpoint } from 'constants/api'
import { MessageTypes } from 'constants/message-screens'
import { MOBILE_BREAKPOINT } from 'constants/global'
import useViewport from 'hooks/use-viewport'
import OverviewTab from './tabs/OverviewTab'
import HeadToHeadTab from './tabs/HeadToHeadTab'
import WatchlistToggle from './ProfileWatchlistToggle'
import { getRuntimeStyles, ONBOARDING_STEPS } from './onboarding-tour/ProfileSteps'
import UserMessageFullScreen from 'components/screens/UserMessageFullScreen'
import { WtnPages } from 'constants/guided-tours'
import { useGuidedTour } from 'hooks/use-guided-tour'
import ResultsTab from './tabs/ResultsTab'
import { TabNavigationModule, TabContent, TabObj } from 'components/generic/TabNavigation'
import LoadingSpinnerWithContainer from 'components/generic/LoadingSpinnerWithContainer'
import UserApi from 'api/rest/user'
import { useProfileParams } from './ProfileRouter/hooks'
import { useProfileTabNavigation } from './hooks'
import { selectProfileAvatarUpdating } from 'redux/selectors'
import EditableAvatar from './EditableAvatar'

const replaceOwnProfileParamWithTennisId = (tennisId: string) => {
  const url = new URL(window.location.href)
  url.searchParams.delete(QueryParamNames.PROFILE_OWN_PROFILE_FLAG)
  url.searchParams.set(QueryParamNames.PROFILE_TENNIS_ID, tennisId)
  window.history.pushState(null, '', url.toString())
}

const RedirectToOwnProfile = () => {
  useEffect(() => {
    UserApi.redirectToOwnProfilePage()
  }, [])
  return <LoadingSpinnerWithContainer fullScreen spinnerWidth={130} />
}

interface Props {
  enableSelfSubmission?: boolean
  hideResultsForAnonUser?: boolean
}

const Profile = ({ enableSelfSubmission, hideResultsForAnonUser }: Props) => {
  const dispatch = useDispatch()
  const { activeSection, handleTabSelect } = useProfileTabNavigation()
  const { hasOwnProfileFlag, playerId, tennisId } = useProfileParams()
  const loadedProfile = useSelector((state: AppState) => state.profile.loadedProfile)
  const profileLoading = useSelector((state: AppState) => state.profile.profileLoading)
  const profileError = useSelector((state: AppState) => state.profile.profileError)
  const errorType = useSelector((state: AppState) => state.profile.profileErrorType)
  const vsPlayerIds = useSelector((state: AppState) => state.h2h.vsPlayerIds)
  const isLoggedIn = useSelector((state: AppState) => state.user.isLoggedIn)
  const ownTennisId: string = useSelector((state: AppState) => state.user.tennisId)
  const profileAvatarUpdating = useSelector(selectProfileAvatarUpdating)
  const isOwnProfile =
    !!loadedProfile?.tennisID && !!ownTennisId && loadedProfile.tennisID === ownTennisId
  const hasPendingMatches = useSelector(
    (state: AppState) =>
      !!state.socialMatches.totals[MY_SOCIAL_MATCHES_KEY]?.PENDING &&
      ownTennisId === loadedProfile?.tennisID
  )
  const { getCountryName } = useCountries()
  const { deviceWidth } = useViewport()

  const NAV_TABS: TabObj[] = useMemo(
    () => [
      {
        id: ProfileSection.OVERVIEW,
        key: ProfileSection.OVERVIEW,
        title: i18n.t('strProfile.sections.overview'),
        className: 'overview-tab',
      },
      {
        id: ProfileSection.RESULTS,
        key: ProfileSection.RESULTS,
        title: i18n.t('strProfile.sections.results'),
        className: 'results-tab',
        hasNotification: hasPendingMatches,
      },
      {
        id: ProfileSection.H2H,
        key: ProfileSection.H2H,
        title: i18n.t('strProfile.sections.h2h'),
        className: 'head-to-head-tab',
      },
    ],
    [hasPendingMatches]
  )

  const availableHeaderInfo = useMemo(() => {
    const info: any[] = []
    const { gender, countryCode, birthYear, age } = loadedProfile || ({} as any)
    const ageReference = getAgeReference(age, birthYear)

    gender && info.push(gender)
    ageReference && info.push(ageReference)
    countryCode &&
      info.push(
        <>
          <Flag alpha3={countryCode} /> {getCountryName(countryCode) || getUnknownDisplayText()}
        </>
      )
    return info
  }, [loadedProfile, getCountryName])

  const { currentStep, GuidedTour, props, tourState } = useGuidedTour(
    ownTennisId,
    WtnPages.PLAYER_PROFILE,
    ONBOARDING_STEPS
  )

  if (currentStep)
    props.styles = { ...props.styles, ...getRuntimeStyles(currentStep.name, deviceWidth) }

  useEffect(() => {
    if (playerId || tennisId) {
      const personIDInput: PersonIDInput = getProfileId(playerId, tennisId)
      dispatch(loadProfile({ id: personIDInput.identifier, type: personIDInput.type, isLoggedIn }))
    } else if (hasOwnProfileFlag && ownTennisId) {
      dispatch(loadProfile({ id: ownTennisId, type: PersonIDEnum.TennisID, isLoggedIn }))
      replaceOwnProfileParamWithTennisId(ownTennisId)
    }
  }, [dispatch, hasOwnProfileFlag, playerId, tennisId, ownTennisId, isLoggedIn])

  if (!playerId && !tennisId && !hasOwnProfileFlag) return <RedirectToOwnProfile />

  if (hasOwnProfileFlag) {
    if (!isLoggedIn) {
      return (
        <UserMessageFullScreen
          messageType={MessageTypes.PAGE_RESTRICTED}
          backLink={getSignInUrl(getOwnProfileUrl())}
        />
      )
    } else if (!ownTennisId) {
      return (
        <UserMessageFullScreen
          messageType={MessageTypes.OWN_PROFILE_ERROR}
          backLink={getSignInUrl(getOwnProfileUrl())}
        />
      )
    }
  }

  if (profileError)
    return (
      <UserMessageFullScreen
        messageType={errorType}
        error={errorType !== MessageTypes.PLAYER_NOT_FOUND ? profileError : undefined}
      />
    )

  if (!loadedProfile) {
    return <LoadingSpinnerWithContainer fullScreen spinnerWidth={130} />
  }

  if (isPlayerHidden(loadedProfile)) {
    return <UserMessageFullScreen messageType={MessageTypes.PLAYER_NOT_FOUND} />
  }

  return (
    <>
      {isLoggedIn && <GuidedTour {...props} />}
      <section className={`${styles.profileHeaderWrapper}`}>
        <MainContainer>
          <div className={`${styles.profileHeaderTop}`}>
            <div className={`${styles.profileHeaderLeft}`}>
              {isOwnProfile ? (
                <EditableAvatar
                  loadedAvatar={loadedProfile?.avatar}
                  newAvatarLoading={profileAvatarUpdating}
                  userBirthDate={loadedProfile.birthDate}
                  appElement={'player-profile'}
                  onModalUploadFile={(file: File) => dispatch(uploadProfileAvatar(file))}
                  onModalUpdateAvatar={(avatarUrl: string) =>
                    dispatch(updateProfileAvatar(avatarUrl))
                  }
                  currentAvatarUrl={loadedProfile?.avatar || null}
                />
              ) : (
                <Avatar
                  url={loadedProfile?.avatar || undefined}
                  size={deviceWidth < MOBILE_BREAKPOINT ? 'md' : 'lg'}
                  classWrapper={loadedProfile?.avatar ? styles.avatar : styles.avatarFallback}
                />
              )}
              <div className={`${styles.playerDetailsHeader} `}>
                <h2 className={`${styles.textWhite} `}>
                  {profileLoading ? (
                    <Spinner className={styles.spinner} animation='grow' />
                  ) : (
                    loadedProfile && loadedProfile.fullName
                  )}
                </h2>
                <p className={`${styles.textWhite} `}>
                  {availableHeaderInfo.map((info, i) => (
                    <React.Fragment key={info}>
                      {i > 0 && <span className={styles.playerStatsPadding}>|</span>}
                      {info}
                    </React.Fragment>
                  ))}
                </p>
                {isLoggedIn && (
                  <WatchlistToggle isDisabled={isOwnProfile} profile={loadedProfile} />
                )}
              </div>
            </div>
            <div className={`wtn-definition-wrapper ${styles.wtnScaleWrapper}`}>
              <div>
                {!isOwnProfile && (
                  <WTNScaleData
                    mode={''}
                    border={false}
                    animDuration={4500}
                    labelsDesktop
                    labelsMobile
                    mobileBreakPoint={767.98}
                    width={340}
                    tennisID={loadedProfile.tennisID || ''}
                    endpointURL={getGraphQlApiEndpoint()}
                    infoModalZIndex={10000}
                  />
                )}

                {isOwnProfile && (
                  <WTNDefinition
                    profileLoading={profileLoading}
                    wtnSingles={loadedProfile.wtnSingles}
                    wtnDoubles={loadedProfile.wtnDoubles}
                    tennisID={loadedProfile.tennisID}
                    eligibleForPsq={!!loadedProfile.eligibleForPsq}
                  />
                )}
              </div>
            </div>
          </div>
        </MainContainer>
      </section>

      <TabNavigationModule
        navId={'profile-tabs'}
        activeTabKey={activeSection}
        tabs={NAV_TABS}
        onTabSelect={handleTabSelect}
        navClass={styles.tabNavContainer}
        withOverheadTabs
      >
        <TabContent tabKey={ProfileSection.OVERVIEW} activeTabKey={activeSection}>
          <MainContainer>
            <OverviewTab
              player={loadedProfile}
              isOwnProfile={loadedProfile?.tennisID === ownTennisId}
              showWtnHistory={isLoggedIn}
            />
          </MainContainer>
        </TabContent>
        <TabContent tabKey={ProfileSection.RESULTS} activeTabKey={activeSection}>
          <MainContainer>
            <ResultsTab
              player={loadedProfile}
              canRunTour={tourState.isComplete}
              enableSelfSubmission={enableSelfSubmission}
              hideResultsForAnonUser={hideResultsForAnonUser}
            />
          </MainContainer>
        </TabContent>
        <TabContent tabKey={ProfileSection.H2H} activeTabKey={activeSection}>
          <MainContainer>
            <HeadToHeadTab
              vsPlayerIds={vsPlayerIds}
              name={loadedProfile && loadedProfile.fullName}
              showWtnHistory={isLoggedIn}
              hideResultsForAnonUser={hideResultsForAnonUser}
            />
          </MainContainer>
        </TabContent>
      </TabNavigationModule>
    </>
  )
}

export default Profile
